import Text from '@ui/text/text/text';
import SettingsToggle from '../../controls/settings-toggle';
import { startTransition, useCallback, useMemo, useOptimistic, useState } from 'react';
import PreshowVideoWidget from './preshow-video-widget';
import {
  getIsSettingsUpdating,
  getWorkingProjectUuid,
  selectProjectSettings,
  selectWorkingProject,
  selectWorkingSession,
} from '@/store/slices/project/selectors';
import { SessionTypesEnum } from '@/types/session.types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  saveFieldOnAllSessions,
  saveWorkingProjectSessionByField,
  updateProjectSettings,
} from '@/store/slices/project';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import { selectFeedbackEnabled, selectShareEnabled, selectShowCalendar } from '@/store/slices/settings/selectors';
import { EventSettings } from '@/types/projects.types';
import { ICalendarDetailMetadata } from '../../settings.types';
import CalendarDetail from './calendar-detail';
import useLanguageParams from '@/utils/use-language-params';
import deepEqual from 'fast-deep-equal';
import UserFeedbackSurveyText from './user-feedback-survey-text';

const ContentWidget = () => {
  const workingSession = useAppSelector(selectWorkingSession);
  const workingProject = useAppSelector(selectWorkingProject);
  const isSettingsUpdating = useAppSelector(getIsSettingsUpdating);
  const reduxShowAddToCalendar = useAppSelector(selectShowCalendar);
  const settings = useAppSelector(selectProjectSettings);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const language = useLanguageParams() ?? 'en';
  const reduxShareEnabled = useAppSelector(selectShareEnabled);
  const reduxFeedbackEnabled = useAppSelector(selectFeedbackEnabled);

  const session = useMemo(() => {
    if (workingSession) return workingSession;
    return workingProject?.sessions[0];
  }, [workingSession, workingProject]);

  const [isOnDemand, setIsOnDemand] = useOptimistic(session?.session_type === SessionTypesEnum.onDemand);
  const [replayOn, setReplayOn] = useOptimistic(!!session?.replay_on);
  const [showAddToCalendar, setShowAddToCalendar] = useOptimistic(reduxShowAddToCalendar);
  const [shareEnabled, setShareEnabled] = useOptimistic(reduxShareEnabled);
  const [feedbackEnabled, setFeedbackEnabled] = useOptimistic(reduxFeedbackEnabled);

  const [metadata, setMetadata] = useState<{ [key: string]: ICalendarDetailMetadata }>({});
  const [savingFeedback, setSavingFeedback] = useState(false);

  const dispatch = useAppDispatch();

  const handleToggle = useCallback(
    (sKey: string, on: boolean) => {
      if (!session) return;
      switch (sKey) {
        case 'on_demand': {
          startTransition(() => {
            setIsOnDemand(on);
            dispatch(
              saveWorkingProjectSessionByField({
                uuid: session.uuid,
                session_type: on ? SessionTypesEnum.onDemand : SessionTypesEnum.broadcast,
              })
            );
          });
          break;
        }
        case 'replay_on': {
          startTransition(() => {
            setReplayOn(on);
            dispatch(
              saveWorkingProjectSessionByField({
                uuid: session.uuid,
                replay_on: on,
              })
            );
          });
          break;
        }
        case 'share_enabled': {
          if (!projectUuid || !settings || !workingProject?.sessions) return;
          startTransition(async () => {
            setShareEnabled(on);
            const promises: unknown[] = [];
            if (workingSession) {
              promises.push(
                dispatch(saveWorkingProjectSessionByField({ enable_share: on, uuid: workingSession.uuid }))
              );
            }
            promises.push(dispatch(saveFieldOnAllSessions({ enable_share: on })));
            await Promise.all(promises);
          });
          break;
        }
        case 'feedback': {
          if (!projectUuid || !settings || !workingProject?.sessions) return;
          startTransition(async () => {
            setSavingFeedback(true);
            setFeedbackEnabled(on);
            const promises: unknown[] = [];
            if (workingSession) {
              promises.push(
                dispatch(saveWorkingProjectSessionByField({ enable_feedback_survey: on, uuid: workingSession.uuid }))
              );
            }
            promises.push(dispatch(saveFieldOnAllSessions({ enable_feedback_survey: on })));
            await Promise.all(promises);
            setSavingFeedback(false);
          });
          break;
        }
        default: {
          console.warn(`Unknown setting key ${sKey}`);
        }
      }
    },
    [
      dispatch,
      session,
      setIsOnDemand,
      setReplayOn,
      setShareEnabled,
      setFeedbackEnabled,
      projectUuid,
      settings,
      workingProject?.sessions,
      workingSession,
    ]
  );

  const handleCalendarToggle = async (_val: string, on: boolean) => {
    if (!settings || isSettingsUpdating || !projectUuid) return;
    const projectSettings: EventSettings = { ...settings, show_add_to_cal_btn: on };
    startTransition(async () => {
      setShowAddToCalendar(on);
      await dispatch(updateProjectSettings({ event_uuid: projectUuid, settings: projectSettings }));
    });
  };

  const handleMetadataChange = (sessionUuid: string, updatedData: ICalendarDetailMetadata) => {
    setMetadata((prev) => ({
      ...prev,
      [sessionUuid]: { ...(prev[sessionUuid] || {}), ...updatedData },
    }));
  };

  const handleCalendarSave = () => {
    if (!session || Object.keys(metadata).length === 0) return;
    dispatch(
      saveWorkingProjectSessionByField({
        uuid: session.uuid,
        calendar_settings: {
          ...session.calendar_settings,
          content: {
            ...session.calendar_settings?.content,
            [language]: {
              ...session.calendar_settings?.content?.[language],
              ...(metadata[session.uuid]?.name && { title: metadata[session.uuid].name }),
              ...(metadata[session.uuid]?.description && { description: metadata[session.uuid].description }),
            },
          },
        },
      })
    );
    setMetadata({});
  };

  const handleCalendarCancel = () => setMetadata({});

  return (
    <>
      <div className="flex-column gap-8">
        <Text className="H5" color="var(--gray-100)">
          Content
        </Text>
        <PreshowVideoWidget />
        {session?.timestamp && (
          <SettingsToggle
            label="Add to Calendar"
            value={showAddToCalendar}
            sKey=""
            gap="0px"
            onToggle={handleCalendarToggle}
          >
            <SlideDownCard autoGrow animateOnInitialLoad={false} open={session && showAddToCalendar}>
              <CalendarDetail
                session={session}
                metadata={metadata[session.uuid] || {}}
                setMetadata={(data) => handleMetadataChange(session.uuid, data)}
                onCancel={handleCalendarCancel}
                onSave={handleCalendarSave}
                saveDisabled={deepEqual(metadata, {})}
              />
            </SlideDownCard>
          </SettingsToggle>
        )}
        <SettingsToggle label="Share" value={!!shareEnabled} sKey="share_enabled" onToggle={handleToggle} />
        <SettingsToggle
          label="Feedback Survey"
          value={!!feedbackEnabled}
          sKey="feedback"
          onToggle={handleToggle}
          gap="0px"
          tooltipText="When your session ends, a 5 star rating modal will populate for all users"
        >
          <SlideDownCard autoGrow animateOnInitialLoad={false} open={!!feedbackEnabled}>
            <UserFeedbackSurveyText saving={savingFeedback} />
          </SlideDownCard>
        </SettingsToggle>
        <SettingsToggle
          label="Auto-Replay"
          value={replayOn}
          sKey={'replay_on'}
          loading={!session}
          onToggle={handleToggle}
          tooltipText="After your livestream ends, immediately populates the video for on demand viewing"
        />
        <SettingsToggle
          disabled={
            ![SessionTypesEnum.broadcast, SessionTypesEnum.onDemand].includes(session?.session_type as SessionTypesEnum)
          }
          label="On Demand"
          value={isOnDemand}
          sKey={'on_demand'}
          loading={!session}
          onToggle={handleToggle}
          tooltipText="Changes date & time module on the Overview page to allow for video upload"
        />
      </div>
    </>
  );
};

export default ContentWidget;
